import { Permission } from '@procureai/permissions';
import { createElement, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgSupplierFindings } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  GridAlignmentType,
  gridColDefFactory,
  LinkCell,
  LinkCellValue,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { RouteId } from '~/routes/RouteId';

export enum InternalSourcerResultDataGridField {
  ID = 'id',
  NAME = 'name',
  PERFORMANCE_SUMMARY = 'performanceSummary',
  RELATIONSHIP_DETAILS = 'relationshipDetails',
}

const CELL_STYLES = {
  whiteSpace: 'normal' as const,
  lineHeight: '1.4',
};

interface PerformanceSummaryData {
  activeContractCount?: number;
  purchaseOrderValueSum6m?: number;
  purchaseOrderValueSum12m?: number;
}

interface RelationshipDetailsData {
  relevantContracts?: { id: string; title: string; summary?: string }[];
  relevantPurchaseOrders?: { id: string; title: string; summary?: string }[];
  relevantSourcingProjects?: { id: string; title: string; summary?: string }[];
}

export const useInternalSourcerResultDataGrid: UseDataGridConfig<SvbrgSupplierFindings> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'InternalSourcerResultDataGrid.column',
  });
  const { t: tContent } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'InternalSourcerResultDataGrid.content',
  });

  return useMemo(() => {
    const createColumn = gridColDefFactory({
      tColumn,
      t,
      defaultOptions: { sortable: false, align: GridAlignmentType.LEFT, minWidth: 120 },
    });
    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        createColumn({
          field: InternalSourcerResultDataGridField.NAME,
          renderCell: cellFactory(LinkCell, { needs: { permission: Permission.BaseSupplierList } }),
          valueGetter: (_value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.supplierDetail(row.id),
            label: row.name,
          }),
          hideable: false,
          minWidth: 200,
          flex: 1,
        }),
        createColumn({
          field: InternalSourcerResultDataGridField.PERFORMANCE_SUMMARY,
          minWidth: 300,
          flex: 2,
          renderCell: ({ value }) => {
            if (!value || typeof value !== 'object') return null;

            const summary = value as PerformanceSummaryData;
            const elements = [];

            if (summary.activeContractCount !== undefined) {
              elements.push(
                createElement(
                  'span',
                  { key: 'contracts' },
                  tContent('activeContracts', { count: summary.activeContractCount }),
                ),
              );
            }

            if (summary.purchaseOrderValueSum6m !== undefined) {
              elements.push(
                createElement('span', { key: '6m' }, [
                  tContent('purchaseOrderValue6m') + ': ',
                  createElement(CurrencyCell, {
                    key: 'currency-6m',
                    value: summary.purchaseOrderValueSum6m,
                    currency: 'EUR',
                  }),
                ]),
              );
            }

            if (summary.purchaseOrderValueSum12m !== undefined) {
              elements.push(
                createElement('span', { key: '12m' }, [
                  tContent('purchaseOrderValue12m') + ': ',
                  createElement(CurrencyCell, {
                    key: 'currency-12m',
                    value: summary.purchaseOrderValueSum12m,
                    currency: 'EUR',
                  }),
                ]),
              );
            }

            // Join elements with bullet separator
            const joined: (string | ReactElement<any>)[] = [];
            elements.forEach((element, index) => {
              if (index > 0) joined.push(' • ');
              joined.push(element);
            });

            return createElement('div', { style: CELL_STYLES }, joined);
          },
        }),
        createColumn({
          field: InternalSourcerResultDataGridField.RELATIONSHIP_DETAILS,
          minWidth: 300,
          flex: 2,
          renderCell: ({ value }) => {
            if (!value || typeof value !== 'object') return null;

            const details = value as RelationshipDetailsData;
            const parts = [];

            if (details.relevantContracts?.length) {
              parts.push(tContent('contractsCount', { count: details.relevantContracts.length }));
            }

            if (details.relevantPurchaseOrders?.length) {
              parts.push(tContent('purchaseOrdersCount', { count: details.relevantPurchaseOrders.length }));
            }

            if (details.relevantSourcingProjects?.length) {
              parts.push(tContent('sourcingProjectsCount', { count: details.relevantSourcingProjects.length }));
            }

            const content = parts.length > 0 ? parts.join(' • ') : tContent('noRelationshipData');

            return createElement('div', { style: CELL_STYLES }, content);
          },
        }),
      ],
    } as DataGridConfig<SvbrgSupplierFindings>;
  }, [tColumn, t, tContent]);
};
